import type { Event } from '../types';
import { HANDOFF_KEY, HANDOFF_TTL_MS } from '../../shared/out.mjs';
import { isAllIn } from '../../shared/providers.mjs';

/**
 * THE HAND-OFF RECORD (2026-10-19). What the price board showed when the visitor pressed "Get
 * tickets" (the board row or the sticky bar): the partner, the price as shown — including the
 * live HelloTickets refresh, which the nightly data behind the /out/ page does not have — its fee
 * basis, the "unusually low" caution and the party size. The /out/ card
 * (src/pages/Go.tsx) repeats exactly that, instead of the nightly price.
 *
 * localStorage, not sessionStorage: the /out/ page opens in a new tab, which starts with an empty
 * sessionStorage. Written by the click, read by the /out/ page, valid for two minutes and cleared
 * after (src/lib/visit.ts prunes a stale one on any page load). A crafted link cannot
 * write it, so a ?price= in the URL stays ignored. Raw USD numbers and ISO dates, never formatted
 * strings: the card formats them in the visitor's language, currency and time zone.
 * Disclosed in the privacy policy (src/pages/Legal.tsx, "Personalisation").
 *
 * The shell's inline script (shared/out.mjs) also reads `event`, `market` and `ts`, to time the
 * redirect from the click itself.
 */
export type Handoff = {
  v: 1;
  /** our event id */
  event: string;
  /** the partner's /out/<market> slug */
  market: string;
  placement: 'board' | 'sticky';
  /** per ticket, USD, as the board showed it */
  price: number | null;
  allIn: boolean | null;
  /** the build's markSuspects flag: the board said "unusually low — confirm on the site" */
  suspect: boolean;
  /** the party size picked on the board (1-20) */
  qty: number | null;
  /** when the click happened (ms since epoch) */
  ts: number;
  name: string;
  artist: string | null;
  date: string;
  time: string | null;
  venue: string;
  city: string;
};

type Pending = Omit<Handoff, 'placement' | 'ts'>;

/** The links the board and the sticky bar rendered, by event and partner name (bounded). */
const pending = new Map<string, Pending>();
const MAX_PENDING = 48;
const keyOf = (eventId: string, marketName: string) => `${eventId}|${marketName}`;

/**
 * goHref (src/data/marketplaces.ts) calls this for every partner link it builds, with the very
 * event object the board renders, so the record carries the price the visitor is looking at.
 * Browser only: a server render has no click to remember.
 */
export const rememberOut = (m: { name: string; slug: string }, event: Event, qty?: number) => {
  if (typeof window === 'undefined') return;
  const offer = event.offers?.find((o) => o.source === m.name) ?? null;
  const rec: Pending = {
    v: 1,
    event: event.id,
    market: m.slug,
    price: offer && offer.from > 0 ? offer.from : null,
    allIn: offer ? isAllIn(offer) : null,
    suspect: !!offer?.suspect,
    qty: typeof qty === 'number' && qty >= 1 && qty <= 20 ? Math.floor(qty) : null,
    name: event.name,
    artist: event.artist ?? null,
    date: event.date,
    time: event.time ?? null,
    venue: event.venue,
    city: event.city,
  };
  const k = keyOf(event.id, m.name);
  pending.delete(k);
  pending.set(k, rec);
  if (pending.size > MAX_PENDING) {
    const oldest = pending.keys().next().value;
    if (oldest !== undefined) pending.delete(oldest);
  }
};

/**
 * The click (trackOutbound, src/lib/track.ts): stores the record of the link that was pressed,
 * stamped now. Never blocks the click; without storage the card shows the nightly price.
 */
export const writeHandoff = (eventId: string, marketName: string, placement?: string) => {
  const rec = pending.get(keyOf(eventId, marketName));
  if (!rec) return;
  try {
    const out: Handoff = { ...rec, placement: placement === 'sticky_bar' || placement === 'sticky' ? 'sticky' : 'board', ts: Date.now() };
    localStorage.setItem(HANDOFF_KEY, JSON.stringify(out));
  } catch {
    /* storage blocked or full */
  }
};

const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 300;
const optStr = (v: unknown): v is string | null => v === null || str(v);

/** A stored record, checked field by field: anything else is not ours to show. */
const parse = (raw: string | null): Handoff | null => {
  if (!raw) return null;
  let r: Record<string, unknown>;
  try {
    r = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!r || typeof r !== 'object' || r.v !== 1) return null;
  const okPrice = r.price === null || (typeof r.price === 'number' && Number.isFinite(r.price) && r.price > 0 && r.price < 1e6);
  const okQty = r.qty === null || (typeof r.qty === 'number' && Number.isInteger(r.qty) && r.qty >= 1 && r.qty <= 20);
  if (
    !str(r.event) ||
    !str(r.market) ||
    !okPrice ||
    !okQty ||
    !(r.allIn === null || typeof r.allIn === 'boolean') ||
    typeof r.suspect !== 'boolean' ||
    typeof r.ts !== 'number' ||
    !str(r.name) ||
    !optStr(r.artist) ||
    !(typeof r.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.date)) ||
    !(r.time === null || (typeof r.time === 'string' && /^\d{1,2}:\d{2}$/.test(r.time))) ||
    !str(r.venue) ||
    !str(r.city)
  )
    return null;
  return { ...(r as Handoff), placement: r.placement === 'sticky' ? 'sticky' : 'board' };
};

const fresh = (h: Handoff, now: number) => now - h.ts >= 0 && now - h.ts <= HANDOFF_TTL_MS;

/**
 * The /out/ page's read, after mount: the record when it names this event and partner and is under
 * two minutes old. Reading leaves it in place (a repeated read — React's development double effects,
 * a reload — gets the same answer, and another tab may be about to read its own); a stale or broken
 * record is cleared, and pruneHandoff clears the rest once their two minutes are up.
 */
export const readHandoff = (eventId: string, market: string, now = Date.now()): Handoff | null => {
  try {
    const raw = localStorage.getItem(HANDOFF_KEY);
    if (raw === null) return null;
    const h = parse(raw);
    if (!h || !fresh(h, now)) {
      localStorage.removeItem(HANDOFF_KEY);
      return null;
    }
    return h.event === eventId && h.market === market ? h : null;
  } catch {
    return null;
  }
};

/** Clears a record past its two minutes (src/lib/visit.ts, on every page load). */
export const pruneHandoff = (now = Date.now()) => {
  try {
    const raw = localStorage.getItem(HANDOFF_KEY);
    if (raw === null) return;
    const h = parse(raw);
    if (!h || !fresh(h, now)) localStorage.removeItem(HANDOFF_KEY);
  } catch {
    /* no storage */
  }
};
